/*
 * Copyright (c) 2025-2026 Terra Technology (Guangzhou) Co., Ltd.
 * Copyright (c) 2024-2026 山东艾坦迪数智科技有限公司.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
 * THE SOFTWARE.
 *
 */

import ModalConfirm from "@/components/ModalConfirm";
import { OperationEnum } from "@/enums";
import { wrapperResult } from '@/utils';
import { ActionType, ProFormInstance } from "@ant-design/pro-components";
import { history, request, useModel } from "@umijs/max";
import { Form, message } from "antd";
import React, { useCallback, useEffect, useRef } from "react";

type Props = {
  entityName?: string;
  pathname: string;
  baseUrl: string;
  onOpenChange?: (visible: boolean) => void;
}

/**
 * useCrud Hook 返回值接口定义
 */
export interface CrudHookResult<T extends Entity> {
  fetchPage: (params: Record<string, any>) => Promise<any>;
  // search: (params: Record<string, any>) => Promise<any>;
  handleSaveOrUpdate: (values: Record<string, any>) => Promise<void>;
  handleCreate: (values: Record<string, any>) => Promise<void>;
  handleUpdate: (values: Record<string, any>) => Promise<void>;
  saveOrUpdate: (data: any) => Promise<API.Result<T>>;
  create: (data: Record<string, any>) => Promise<API.Result<T>>;
  update: (id: React.Key, data: Record<string, any>) => Promise<API.Result<T>>;
  deleteById: (id: any) => Promise<API.Result<void>>;
  batchDeleteByIds: (ids: any[]) => Promise<API.Result<void>>;
  toCreate: (initialData?: any) => void;
  toEdit: (editData?: T) => void;
  toDialog: (editData: T) => void;
  toDelete: (id: any, refresh?: boolean) => Promise<void>;
  toBatchDelete: (ids: (number | string)[], refresh?: boolean) => Promise<void>;
  toPage: (editData: T, targetPath: string) => void;
  setDialogVisible: (visible: boolean) => void;
  setLoading: (loading: boolean) => void;
  setDialogTitle: (title: string) => void;
  setShouldRefresh: (shouldRefresh: boolean) => void;
  setEditData: (editData: T | null) => void;
  form: import('antd').FormInstance;
  formRef: React.MutableRefObject<ProFormInstance | undefined>;
  actionRef: React.MutableRefObject<ActionType | undefined>;
  getState: (pathname: string) => any;
  updateState: (pathname: string, newState: Partial<any>) => void;
  resetState: (pathname: string, full?: boolean) => void;
}

/**
 * 通用 CRUD Hook
 *
 * 提供标准的增删改查操作和 UI 状态管理
 *
 * API 端点约定：
 * - GET    baseUrl          → 分页查询
 * - GET    baseUrl/search   → 复杂搜索
 * - POST   baseUrl          → 简单新增/更新 (saveOrUpdate)
 * - POST   baseUrl/create   → 复杂新增 (create)
 * - PUT    baseUrl          → 复杂更新 (update)
 * - DELETE baseUrl/{id}     → 删除
 */
export default function useCrud<T extends Entity>({ entityName, pathname, baseUrl, onOpenChange }: Props): CrudHookResult<T> {
  const { getState, initState, updateState, resetState } = useModel('crudModel');

  // 使用 Ant Design 的 useForm，这在 React 渲染周期内是绝对稳定的
  const [form] = Form.useForm();
  // @ts-ignore
  const formRef = useRef<ProFormInstance>();
  const actionRef = useRef<ActionType | undefined>();

  // 初始化对应 pathname 的状态，并在组件卸载时重置对话框状态
  useEffect(() => {
    initState(pathname);
    // 组件卸载时重置对话框状态，解决页面切换时对话框残留问题
    return () => {
      resetState(pathname);
    };
  }, [pathname, initState, resetState]);

  // ============================================================================
  // 纯 API 调用方法 (第 4 层)
  // ============================================================================

  /**
   * 分页查询
   */
  const findByPage = useCallback(async (params: Record<string, any>) => {
    return request<API.Result<API.PageResult<T>>>(
      baseUrl,
      {
        method: 'GET',
        params
      }
    )
  }, [baseUrl]);

  /**
   * 简单新增或更新 (后端根据 id 判断)
   */
  const saveOrUpdate = useCallback(async (data: any) => {
    if (data.id) {
      return request<API.Result<T>>(
        `${baseUrl}/${data.id}`,
        {
          method: 'PUT',
          data
        }
      )
    }
    return request<API.Result<T>>(
      baseUrl,
      {
        method: 'POST',
        data
      }
    )
  }, [baseUrl]);

  /**
   * 复杂新增 (有特殊业务逻辑)
   */
  const create = useCallback(async (data: Record<string, any>) => {
    return request<API.Result<T>>(
      baseUrl + '/create',
      {
        method: 'POST',
        data
      }
    )
  }, [baseUrl]);

  /**
   * 复杂更新 (有特殊业务逻辑)
   */
  const update = useCallback(async (id: React.Key, data: Record<string, any>) => {
    return request<API.Result<T>>(
      `${baseUrl}/${id}`,
      {
        method: 'PUT',
        data
      }
    )
  }, [baseUrl]);

  /**
   * 根据 ID 删除
   */
  const deleteById = useCallback(async (id: any) => {
    return request<API.Result<void>>(
      baseUrl + '/' + id,
      {
        method: 'DELETE'
      })
  }, [baseUrl]);

  /**
   * 批量删除
   */
  const batchDeleteByIds = useCallback(async (ids: any[]) => {
    return request<API.Result<void>>(
      baseUrl,
      {
        method: 'DELETE',
        data: ids
      })
  }, [baseUrl]);

  // ============================================================================
  // 封装方法 (供 ProTable 使用)
  // ============================================================================

  /**
   * 分页查询 (适配 ProTable request)
   */
  const fetchPage = useCallback(async (params: Record<string, any>) => {
    const { current, pageSize, ...rest } = params;
    const result = await findByPage({
      pageNumber: (current || 1) - 1,
      pageSize,
      ...rest
    });
    return wrapperResult(result);
  }, [findByPage]);

  /**
   * 复杂搜索 (适配 ProTable request，当前与 fetchPage 一致)
   */
  // const search = fetchPage;

  // ============================================================================
  // 带 UI 交互的操作方法 (第 1 层)
  // ============================================================================

  /**
   * 处理简单保存/更新 (带 loading、message、刷新)
   */
  const handleSaveOrUpdate = useCallback(async (values: Record<string, any>) => {
    return new Promise<void>(async (resolve, reject) => {
      try {
        updateState(pathname, { loading: true });
        const result = await saveOrUpdate(values);
        void message.success(result.message || '保存成功');
        updateState(pathname, { loading: false, shouldRefresh: true, dialogVisible: false });
        onOpenChange?.(false);
        resolve();
      } catch (error: any) {
        updateState(pathname, { loading: false });
        console.error(error);
        reject(error);
      }
    });
  }, [saveOrUpdate, pathname, updateState, onOpenChange]);

  /**
   * 处理复杂新增 (带 loading、message、刷新)
   */
  const handleCreate = useCallback(async (values: Record<string, any>) => {
    return new Promise<void>(async (resolve, reject) => {
      try {
        updateState(pathname, { loading: true });
        const result = await create(values);
        void message.success(result.message || '创建成功');
        updateState(pathname, { loading: false, shouldRefresh: true, dialogVisible: false });
        onOpenChange?.(false);
        resolve();
      } catch (error: any) {
        updateState(pathname, { loading: false });
        console.error(error);
        reject(error);
      }
    });
  }, [create, pathname, updateState, onOpenChange]);

  /**
   * 处理复杂更新 (带 loading、message、刷新)
   */
  const handleUpdate = useCallback(async (values: Record<string, any>) => {
    return new Promise<void>(async (resolve, reject) => {
      try {
        updateState(pathname, { loading: true });
        const state = getState(pathname);
        // 自动合并逻辑
        const data = state?.editData ? { ...state.editData, ...values } : values;

        if (!data.id) {
          throw new Error('Update requires an ID');
        }
        const result = await update(data.id, data);
        void message.success(result.message || '更新成功');
        updateState(pathname, { loading: false, shouldRefresh: true, dialogVisible: false });
        onOpenChange?.(false);
        resolve();
      } catch (error: any) {
        updateState(pathname, { loading: false });
        console.error(error);
        reject(error);
      }
    });
  }, [update, pathname, updateState, onOpenChange, getState]);

  // ============================================================================
  // UI 状态变化方法 (第 2 层)
  // ============================================================================

  /**
   * 打开新建对话框
   */
  const toCreate = useCallback((initialData?: any) => {
    updateState(pathname, {
      operation: OperationEnum.CREATE,
      dialogTitle: '新建' + entityName,
      dialogVisible: true,
      editData: initialData || null
    })
  }, [entityName, pathname, updateState]);

  /**
   * 打开编辑对话框
   */
  const toEdit = useCallback((editData?: T) => {
    updateState(pathname, {
      operation: OperationEnum.EDIT,
      dialogTitle: '编辑' + entityName,
      dialogVisible: true,
      editData: editData ? { ...editData } : null
    });
  }, [entityName, pathname, updateState]);

  /**
   * 跳转到详情页面
   */
  const toPage = useCallback((editData: T, targetPath: string) => {
    updateState(targetPath, {
      operation: OperationEnum.EDIT,
      editData: { ...editData }
    });
    history.push(targetPath);
  }, [updateState]);

  /**
   * 打开对话框（通用）
   */
  const toDialog = useCallback((editData: T) => {
    updateState(pathname, {
      editData: { ...editData }
    });
    onOpenChange?.(true);
  }, [pathname, updateState, onOpenChange]);

  /**
   * 删除确认
   */
  const toDelete = useCallback((id: any, refresh: boolean = false) => {
    return new Promise<void>(async (resolve, reject) => {
      try {
        const result = await deleteById(id);
        message.success(result.message || '删除成功');
        if (refresh) {
          updateState(pathname, { shouldRefresh: true });
        }
        resolve();
      } catch (error: any) {
        console.error(error.message);
        reject(error);
      }
    });
  }, [pathname, updateState, deleteById]);

  /**
   * 批量删除确认
   */
  const toBatchDelete = useCallback((ids: (number | string)[], refresh: boolean = false) => {
    return new Promise<void>((resolve, reject) => {
      if (!ids || ids.length === 0) {
        void message.warning('请选择要删除的数据');
        reject(new Error('未选择数据'));
        return;
      }

      ModalConfirm({
        title: '批量删除' + entityName,
        content: `确定要删除选中的 ${ids.length} 条${entityName || '数据'}吗？删除后将无法恢复。`,
        async onOk() {
          try {
            const result = await batchDeleteByIds(ids);
            void message.success(result.message || '批量删除成功');
            if (refresh) {
              updateState(pathname, { shouldRefresh: true });
            }
            resolve();
          } catch (error: any) {
            console.error(error.message);
            reject(error);
          }
        },
        onCancel() {
          // 用户取消，不做处理
        }
      });
    });
  }, [pathname, updateState, entityName, batchDeleteByIds]);

  // ============================================================================
  // 状态设置方法 (第 3 层)
  // ============================================================================

  const setDialogVisible = useCallback((visible: boolean) => {
    updateState(pathname, { dialogVisible: visible })
  }, [pathname, updateState]);

  const setLoading = useCallback((loading: boolean) => {
    updateState(pathname, { loading })
  }, [pathname, updateState]);

  const setDialogTitle = useCallback((title: string) => {
    updateState(pathname, { dialogTitle: title });
  }, [pathname, updateState]);

  const setShouldRefresh = useCallback((shouldRefresh: boolean) => {
    updateState(pathname, { shouldRefresh });
  }, [pathname, updateState]);

  const setEditData = useCallback((editData: T | null) => {
    updateState(pathname, { editData });
  }, [pathname, updateState]);

  return {
    fetchPage,
    // search,
    handleSaveOrUpdate,
    handleCreate,
    handleUpdate,
    saveOrUpdate,
    create,
    update,
    deleteById,
    batchDeleteByIds,
    toCreate,
    toEdit,
    toDialog,
    toDelete,
    toBatchDelete,
    toPage,
    setDialogVisible,
    setLoading,
    setDialogTitle,
    setShouldRefresh,
    setEditData,
    form,
    formRef,
    actionRef,
    getState,
    updateState,
    resetState
  }
}
